<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { formatNumber } from '@/app/core/util/numbers'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  DTextAreaField,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import {
  getBenefits,
  Benefits,
  deleteBenefit
} from '@/app/core/services/benefits'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref()
const acceptQuestion = ref()

const loading = ref(false)

/* Datos */
const search = ref()
const tableBenefit = ref(null)
const benefitForDelete = ref()
const causeOfElimination = ref('')
const validObservation = ref(true)

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('benefits.list.code'), table: 'code' },
  { name: 'name', title: t('benefits.list.name'), table: 'name' },
  { name: 'from', title: t('benefits.list.from'), table: 'benefits.from' },
  { name: 'to', title: t('benefits.list.to'), table: 'benefits.to' },
  {
    name: 'listPayrolls',
    title: t('benefits.list.payrolls'),
    table: 'payrolls'
  },
  {
    name: 'listSucursals',
    title: t('benefits.list.sucursals'),
    table: 'sucursals'
  },
  {
    name: 'listDepartments',
    title: t('benefits.list.departments'),
    table: 'departments'
  },
  {
    name: 'listPositions',
    title: t('benefits.list.positions'),
    table: 'positions'
  },
  {
    name: 'value',
    title: t('benefits.list.value'),
    format: (value: any) => formatNumber(value),
    align: 'end',
    table: 'value'
  },
  {
    name: 'updated_at',
    title: t('benefits.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<Benefits[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createBenefit = () => {
  void router.push({ name: 'CreateBenefit' })
}
const updateBenefit = (id: any) => {
  void router.push({ name: 'UpdateBenefit', params: { id } })
}
const getBenefit = (id: any) => {
  void router.push({ name: 'GetBenefit', params: { id } })
}

/* Eliminar beneficio */
const removeBenefit = (values: any) => {
  showModal.value = true
  titleModal.value = t('benefits.list.actionStatus', {
    status: t('table.action.delete'),
    benefit: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  benefitForDelete.value = values.id
}
const deleteConfirm = async () => {
  if (
    causeOfElimination.value.length < 20 ||
    causeOfElimination.value.length > 200
  ) {
    validObservation.value = false
  } else {
    validObservation.value = true
    loading.value = true
    showModal.value = false
    await deleteBenefit(
      { causeOfElimination: causeOfElimination.value },
      benefitForDelete.value
    )
      .then(response => {
        loading.value = false
        notifications.addNotification({
          title: t('notification.removed'),
          text: t('benefits.notification.action', [
            t('notification.wasRemoved')
          ]),
          type: 'success',
          time: 10000
        })
        causeOfElimination.value = ''
        void benefits()
      })
      .catch(async (error: any) => {
        notificationError(error, 'benefits', t, notifications, loading)
      })
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  validObservation.value = true
  causeOfElimination.value = ''
}

/* Listar beneficios */
const benefits = async () => {
  loading.value = true
  try {
    const data: any = await getBenefits(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'benefits', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()">
    <template #form>
      <DTextAreaField
        v-model="causeOfElimination"
        class="pt-3 pb-1.5 w-9/12"
        :label="t('benefits.list.delete')"
        :resize="false"
        :required="true"
        :error="
          validObservation
            ? ''
            : `${t('form.valid.minLength', [20])}, 
            ${t('form.valid.maxLength', [200])}`
        "
        @input="
          validObservation =
            causeOfElimination.length > 200 || causeOfElimination.length < 20
              ? (validObservation = false)
              : (validObservation = true)
        "
        :rows="5" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('benefits.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('benefits.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="benefits" />
          <DBtn @click.prevent="benefits" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createBenefit()"
          v-if="store.hasPermission('benefit:create')">
          {{ t('form.button.create', { form: t('benefits.list.title', 0) }) }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableBenefit"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), benefits()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), benefits()">
      <template #listPayrolls="data: any">
        <div class="flex items-center">
          <div v-if="data.payroll != ' - '">{{ data.payroll }}</div>
          <div
            v-if="data.payrolls_number > 0"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.payrolls_number }}+
          </div>
        </div>
      </template>
      <template #listDepartments="data: any">
        <div class="flex items-center">
          <div v-if="data.department != ' - '">{{ data.department }}</div>
          <div
            v-if="data.departments_number > 0"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.departments_number }}+
          </div>
        </div>
      </template>
      <template #listPositions="data: any">
        <div class="flex items-center">
          <div v-if="data.position != ' - '">{{ data.position }}</div>
          <div
            v-if="data.positions_number > 0"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.positions_number }}+
          </div>
        </div>
      </template>
      <template #listSucursals="data: any">
        <div class="flex items-center">
          <div v-if="data.sucursal != ' - '">{{ data.sucursal }}</div>
          <div
            v-if="data.sucursals_number > 0"
            class="bg-blue-50 rounded-lg text-blue-700 text-sm font-semibold p-0.5 ml-2 h-6">
            {{ data.sucursals_number }}+
          </div>
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('benefit:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getBenefit(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('benefit:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateBenefit(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('benefit:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeBenefit(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
